import React, {useCallback, useContext, useEffect, useRef, useState} from 'react';
import { Helmet } from 'react-helmet-async';
import { Navigate, useLocation, useNavigate } from 'react-router';
import { useSearchParams } from "react-router-dom";
import { AppContext } from "../../context/AppContext";
import { ResetPasswordComponent } from "../../components/ResetPasswordComponent/ResetPasswordComponent";
import { AuthSsoProvider } from "../../services/AuthService";
import PartnerWall from "../../components/PartnerWall";
import {ImpersonateDestroy} from "../../components/Impersonation";
import {
  isTwoFactorRequired,
  LoginSuccessResponse,
  TwoFactorMethod
} from "../../services/TwoFactorService";
import {
  getCredential,
  isConditionalMediationAvailable,
  isWebAuthnSupported
} from "../../utils/webauthn";

/**
 * which of the three radio driven tabs of the login card is open.
 * 'none' reproduces the old behaviour on a route that is neither the login nor
 * the password reset (the sso redirect), where both radios used to stay
 * unchecked
 */
type LoginTab = 'none' | 'login' | 'reset' | 'twofactor';

/**
 * where the "trust this device for 30 days" token lives. localStorage and not a
 * cookie: the api runs on a different origin, so a cookie set by the spa would
 * never reach it anyway and it has to travel in the json body regardless
 */
const DEVICE_TOKEN_KEY = 'interlink.twofactor.device_token';

/**
 * readDeviceToken
 * localStorage throws in private mode and in embedded webviews, and a missing
 * device token is a perfectly normal state - so it never becomes an error
 */
function readDeviceToken(): string {
  try {
    return window.localStorage.getItem(DEVICE_TOKEN_KEY) || '';
  } catch (error) {
    return '';
  }
}

/**
 * writeDeviceToken
 * @param token empty string removes the entry
 */
function writeDeviceToken(token: string): void {
  try {
    if (token) {
      window.localStorage.setItem(DEVICE_TOKEN_KEY, token);
    } else {
      window.localStorage.removeItem(DEVICE_TOKEN_KEY);
    }
  } catch (error) {
    /** storing the convenience token is never worth failing a login for */
  }
}

/**
 * isUserCancelled
 *
 * the webauthn dialog reports an abort by the user with exactly these two
 * names. it is a decision, not a failure, and showing an error for it would
 * punish the user for closing a dialog they did not ask for - which is exactly
 * what conditional mediation opens on its own.
 *
 * @param error
 */
function isUserCancelled(error: any): boolean {
  const name = error && error.name ? String(error.name) : '';

  return name === 'NotAllowedError' || name === 'AbortError';
}

/**
 * httpStatus
 * the http service rejects with the axios error, so the status the backend
 * really answered sits in response.status. 0 means "never got that far"
 * @param error
 */
function httpStatus(error: any): number {
  return (error && error.response && Number(error.response.status)) || 0;
}

/**
 * retryAfterSeconds
 * the 429 carries the wait both in the body and in the header; the body wins
 * because it is the value the backend computed
 * @param error
 */
function retryAfterSeconds(error: any): number {
  const response = (error && error.response) || {},
    body = response.data || {},
    headers = response.headers || {},
    fromBody = Number(body.retry_after),
    fromHeader = Number(headers['retry-after'] || headers['Retry-After']);

  if (fromBody > 0) {
    return Math.ceil(fromBody);
  }

  return fromHeader > 0 ? Math.ceil(fromHeader) : 0;
}

/**
 * LoginPage
 * @constructor
 */
export function LoginPage() {
  const {
    services: {
      serviceAuth,
      serviceTwoFactor
    },
    I18N,
    isLoggedIn,
    getRoutes,
  } = useContext(AppContext);

  const ROUTE_CONFIG = getRoutes();
  const [ email, setEmail ] = useState('');
  const [ password, setPassword ] = useState('');
  const [ loginError, setLoginError ] = useState<string>('');
  const navigate = useNavigate();
  const location = useLocation();
  const [ searchParams] = useSearchParams();

  /**
   * the open tab is state now instead of defaultChecked, because the second
   * factor step has to be able to open it - and, more importantly, to hold it:
   * while a challenge is running the other two radios are disabled, so neither a
   * click nor the tab key can drop the user back into the password form and
   * silently strand the challenge
   */
  const [ activeTab, setActiveTab ] = useState<LoginTab>(() => {
    if (location.pathname.indexOf(ROUTE_CONFIG.PASSWORD_RESET.path) !== -1) {
      return 'reset';
    }

    return location.pathname.indexOf(ROUTE_CONFIG.LOGIN.path) !== -1 ? 'login' : 'none';
  });

  /** the running challenge. an empty token means: no second factor pending */
  const [ challengeToken, setChallengeToken ] = useState<string>('');
  const [ methods, setMethods ] = useState<TwoFactorMethod[]>([]);
  const [ code, setCode ] = useState('');
  const [ recoveryCode, setRecoveryCode ] = useState('');
  const [ useRecovery, setUseRecovery ] = useState(false);
  const [ rememberDevice, setRememberDevice ] = useState(false);
  const [ twoFactorError, setTwoFactorError ] = useState<string>('');
  const [ twoFactorStatus, setTwoFactorStatus ] = useState<string>('');
  const [ busy, setBusy ] = useState(false);

  /**
   * webauthn is only offered when the browser can actually do it. read once into
   * state so the markup does not change between renders
   */
  const [ webAuthnAvailable ] = useState<boolean>(() => isWebAuthnSupported());
  const [ conditionalActive, setConditionalActive ] = useState(false);

  const codeRef = useRef<HTMLInputElement | null>(null);
  const recoveryRef = useRef<HTMLInputElement | null>(null);
  const passkeyRef = useRef<HTMLButtonElement | null>(null);

  /**
   * the conditional (autofill) passkey request runs for as long as the page is
   * open. the controller ends it on unmount and before any explicit passkey
   * request, so a silent request can never outlive the screen that started it
   */
  const conditionalAbortRef = useRef<AbortController | null>(null);

  /**
   * the conditional request must fire exactly once per visit, so its effect
   * cannot depend on applySession: that callback depends on ROUTE_CONFIG, and
   * getRoutes() builds a fresh object on every render. the effect would re-run,
   * ask for another challenge, re-render through setConditionalActive - and go
   * round again until the rate limiter answers 429. the ref carries the current
   * callback into an effect that only watches webAuthnAvailable.
   */
  const applySessionRef = useRef<((response: LoginSuccessResponse) => void) | null>(null);
  /** react runs effects twice on mount in development strict mode */
  const conditionalStartedRef = useRef(false);

  const hasTotp = methods.indexOf('totp') !== -1;
  const hasRecovery = methods.indexOf('recovery') !== -1;
  const hasWebAuthn = methods.indexOf('webauthn') !== -1 && webAuthnAvailable;
  const inChallenge = challengeToken !== '';

  /**
   * applySession
   * the one place a finished authentication is turned into a session, so the
   * order (token before user) and the device token handling cannot drift apart
   * between the five endpoints that can end a login
   * @param response
   */
  const applySession = useCallback((response: LoginSuccessResponse) => {
    /**
     * only present when remember_device was asked for; keeping the old one
     * otherwise is correct, it is still valid
     */
    if (response.device_token) {
      writeDeviceToken(response.device_token);
    }

    serviceTwoFactor.applySession(response.user, response.authorisation.token);
    navigate(ROUTE_CONFIG.DASHBOARD.path);
  }, [serviceTwoFactor, navigate, ROUTE_CONFIG.DASHBOARD.path]);

  /**
   * resetChallenge
   * back to the password form, challenge discarded
   * @param notice shown on the password form, empty for a plain cancel
   */
  const resetChallenge = useCallback((notice: string = '') => {
    setChallengeToken('');
    setMethods([]);
    setCode('');
    setRecoveryCode('');
    setUseRecovery(false);
    setRememberDevice(false);
    setTwoFactorError('');
    setTwoFactorStatus('');
    setLoginError(notice);
    setActiveTab('login');
  }, []);

  /**
   * handleChallengeError
   *
   * the three cases the backend really distinguishes. 410 is the only one that
   * ends the step: the challenge is gone, so staying on a form that can no
   * longer be submitted would be a dead end.
   *
   * @param error
   */
  const handleChallengeError = useCallback((error: any) => {
    const status = httpStatus(error);

    if (status === 410) {
      resetChallenge(I18N.t("Die Anmeldung ist abgelaufen. Bitte melden Sie sich erneut an."));

      return;
    }

    if (status === 429) {
      const seconds = retryAfterSeconds(error);

      /**
       * I18N.t(text, args) crashes on a broken sprintf import, so the
       * placeholder is replaced here
       */
      setTwoFactorError(seconds > 0
        ? I18N.t("Zu viele Versuche. Bitte warten Sie %s Sekunden.")
          .replace('%s', String(seconds))
        : I18N.t("Zu viele Versuche. Bitte warten Sie einen Moment."));

      return;
    }

    if (status === 401) {
      setTwoFactorError(I18N.t("Der Code ist nicht korrekt."));

      return;
    }

    setTwoFactorError(I18N.t("Die Bestätigung ist fehlgeschlagen. Bitte versuchen Sie es erneut."));
  }, [I18N, resetChallenge]);

  /**
   * startChallenge
   * @param token
   * @param available
   */
  const startChallenge = useCallback((token: string, available: TwoFactorMethod[]) => {
    /**
     * a device token that did not spare the second factor is stale, otherwise it
     * would be offered again on every following login for nothing
     */
    writeDeviceToken('');

    setChallengeToken(token);
    setMethods(available);
    setCode('');
    setRecoveryCode('');
    setUseRecovery(false);
    setTwoFactorError('');
    setLoginError('');
    setTwoFactorStatus(I18N.t("Zweiter Faktor erforderlich."));
    setActiveTab('twofactor');
  }, [I18N]);

  /**
   * handleSSOLogin
   * @param event
   * @param provider
   */
  const handleSSOLogin = async (event: React.MouseEvent<HTMLButtonElement, MouseEvent>, provider: AuthSsoProvider) => {
    event.preventDefault();
    serviceAuth.ssoProvider(provider, ROUTE_CONFIG.LOGIN_SSO.path);
  };

  /**
   * handleSSOLoginRedirect
   */
  const handleSSOLoginRedirect = useCallback(async () => {
    const token: string | null = searchParams.get('token'),
      state: string | null = searchParams.get('state'),
      message: string | null = searchParams.get('message');

    await serviceAuth.ssoLogin(token, state, message);

    navigate(ROUTE_CONFIG.DASHBOARD.path);
  }, [serviceAuth, ROUTE_CONFIG.DASHBOARD.path, navigate, searchParams]);

  useEffect(() => {
    if (!isLoggedIn()) {
      ImpersonateDestroy();
    }
  }, [isLoggedIn]);

  /**
   * handle SSO redirect
   */
  useEffect(() => {
    if (location.pathname.includes(ROUTE_CONFIG.LOGIN_SSO.path)) {
      try {
        handleSSOLoginRedirect();
      } catch(error) {
        console.error(error);
        setLoginError(I18N.t("SSO-Anmeldung fehlgeschlagen. Bitte versuchen Sie es erneut."));
      }
    }
  }, [ROUTE_CONFIG.LOGIN_SSO.path, handleSSOLoginRedirect, location.pathname]);

  /**
   * conditional mediation
   *
   * asks for a passkey the moment the page opens, but inside the browsers own
   * autofill dropdown instead of a modal - the user sees nothing until they
   * touch the e-mail field. every failure and every refusal is swallowed on
   * purpose: this path is an offer, the password form below it stays the
   * supported way in.
   */
  applySessionRef.current = applySession;

  useEffect(() => {
    if (!webAuthnAvailable || conditionalStartedRef.current) {
      return;
    }

    conditionalStartedRef.current = true;

    const controller = new AbortController();

    conditionalAbortRef.current = controller;

    const run = async () => {
      if (!await isConditionalMediationAvailable() || controller.signal.aborted) {
        return;
      }

      setConditionalActive(true);

      try {
        const challenge = await serviceTwoFactor.passwordlessOptions();

        if (controller.signal.aborted) {
          return;
        }

        /**
         * the signal goes all the way into navigator.credentials.get: without
         * it only our side of the request would end and the browser would keep
         * the silent one pending, which makes the next explicit passkey request
         * fail with "a request is already pending"
         */
        const credential = await getCredential(challenge.publicKey, true, controller.signal);

        if (controller.signal.aborted) {
          return;
        }

        const response = await serviceTwoFactor.passwordlessVerify(
          challenge.challengeToken,
          credential
        );

        if (controller.signal.aborted) {
          return;
        }

        applySessionRef.current?.(response);
      } catch (error) {
        /** silent by design, see above */
      }
    };

    run();

    return () => {
      controller.abort();
      conditionalAbortRef.current = null;
      /** a real unmount may be followed by a real mount - allow one more start */
      conditionalStartedRef.current = false;
    };
    /**
     * deliberately only webAuthnAvailable: see applySessionRef above. adding the
     * callbacks back here reopens the request loop.
     */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [webAuthnAvailable]);

  /**
   * focus follows the step
   * without this the focus stays on the (now disabled) submit button of the
   * password form and a screen reader user never learns that a second factor is
   * being asked for
   */
  useEffect(() => {
    if (activeTab !== 'twofactor') {
      return;
    }

    const target = codeRef.current || recoveryRef.current || passkeyRef.current;

    if (target) {
      target.focus();
    }
  }, [activeTab, useRecovery]);

  /** Login */
  const handleLogin = async (event) => {
    event.preventDefault();

    if (busy) {
      return;
    }

    setBusy(true);
    setLoginError('');

    try {
      /**
       * deliberately not serviceAuth.login(): that implementation reads
       * response.data.authorisation.token unconditionally and turns the
       * two_factor_required answer into a TypeError
       */
      const response = await serviceTwoFactor.login(email, password, readDeviceToken());

      if (isTwoFactorRequired(response)) {
        startChallenge(response.challenge_token, response.methods || []);

        return;
      }

      applySession(response);
    } catch (error) {
      console.error('Login Error:', error);

      setLoginError(httpStatus(error) === 401
        ? I18N.t("E-Mail-Adresse oder Passwort ist nicht korrekt.")
        // @ts-ignore
        : I18N.t('Anmeldung fehlgeschlagen: ') + error.message);
    } finally {
      setBusy(false);
    }
  };

  /**
   * handleTwoFactorSubmit
   * one submit for both code fields - which endpoint it goes to is decided by
   * the field the user is looking at, not by a second button
   * @param event
   */
  const handleTwoFactorSubmit = async (event) => {
    event.preventDefault();

    if (busy || !challengeToken) {
      return;
    }

    const value = (useRecovery ? recoveryCode : code).trim();

    if (!value) {
      return;
    }

    setBusy(true);
    setTwoFactorError('');
    setTwoFactorStatus(I18N.t("Der Code wird geprüft."));

    try {
      const response = useRecovery
        ? await serviceTwoFactor.verifyRecovery(challengeToken, value, rememberDevice)
        : await serviceTwoFactor.verifyTotp(challengeToken, value, rememberDevice);

      setTwoFactorStatus(I18N.t("Bestätigt. Sie werden angemeldet."));
      applySession(response);
    } catch (error) {
      setTwoFactorStatus('');
      handleChallengeError(error);
    } finally {
      setBusy(false);
    }
  };

  /**
   * handleSecondFactorPasskey
   * the passkey as second factor: the user is already known through the
   * challenge token, so the backend can name the credentials it accepts
   */
  const handleSecondFactorPasskey = async () => {
    if (busy || !challengeToken) {
      return;
    }

    /**
     * the silent autofill request has to end before a modal one starts,
     * otherwise the browser has two outstanding requests for the same page
     */
    if (conditionalAbortRef.current) {
      conditionalAbortRef.current.abort();
    }

    setBusy(true);
    setTwoFactorError('');
    setTwoFactorStatus(I18N.t("Warten auf die Bestätigung durch Ihren Passkey."));

    try {
      const challenge = await serviceTwoFactor.webauthnAssertionOptions(challengeToken),
        credential = await getCredential(challenge.publicKey),
        response = await serviceTwoFactor.webauthnAssertionVerify(
          challenge.challengeToken,
          credential,
          rememberDevice
        );

      setTwoFactorStatus(I18N.t("Bestätigt. Sie werden angemeldet."));
      applySession(response);
    } catch (error) {
      setTwoFactorStatus('');

      /** the user closed the dialog - that is an answer, not a failure */
      if (isUserCancelled(error)) {
        return;
      }

      handleChallengeError(error);
    } finally {
      setBusy(false);
    }
  };

  /**
   * handlePasswordlessPasskey
   * the passwordless first factor. the user is only established by the
   * assertion, so the challenge is created by the options call itself
   */
  const handlePasswordlessPasskey = async () => {
    if (busy) {
      return;
    }

    if (conditionalAbortRef.current) {
      conditionalAbortRef.current.abort();
    }

    setBusy(true);
    setLoginError('');

    try {
      const challenge = await serviceTwoFactor.passwordlessOptions(),
        credential = await getCredential(challenge.publicKey),
        response = await serviceTwoFactor.passwordlessVerify(
          challenge.challengeToken,
          credential
        );

      applySession(response);
    } catch (error) {
      if (isUserCancelled(error)) {
        return;
      }

      const status = httpStatus(error);

      setLoginError(status === 401 || status === 410
        ? I18N.t("Der Passkey wurde nicht erkannt. Bitte melden Sie sich mit Ihrem Passwort an.")
        : I18N.t("Die Anmeldung mit Passkey ist fehlgeschlagen."));
    } finally {
      setBusy(false);
    }
  };

  // a signed-in visitor used to get an empty page here; the dashboard is where
  // they were headed anyway
  if (isLoggedIn()) {
    return <Navigate replace to={ ROUTE_CONFIG.DASHBOARD.path } />;
  }

  return (
    <>
      <Helmet>
        <title>{I18N.t("Anmelden")}</title>
        <meta name="description" content={I18N.t("Melden Sie sich in Ihrem Konto an")} />
      </Helmet>

        {/* Login-Card */}
        <dia-tabs data-login-card>
          <input
            type="radio"
            name="tabset"
            id="dia-tab1"
            aria-controls="dia-tab-panel1"
            checked={ activeTab === 'login' }
            /** locked while a challenge runs, see activeTab */
            disabled={ inChallenge }
            onChange={ () => setActiveTab('login') }
          />

          <label htmlFor="dia-tab1">{I18N.t("Anmelden")}</label>
          <dia-tabs-panel id="dia-tab-panel1">
            <dia-grid-nested>
              <dia-form>
                <form onSubmit={handleLogin}>
                  <dia-form-area>
                    <dia-form-line>
                      <dia-form-control>
                        <input
                          type="email"
                          name="email"
                          placeholder={I18N.t("Geben Sie Ihre E-Mail-Adresse ein")}
                          value={email}
                          /**
                           * "username webauthn" is what makes the browser offer a
                           * passkey inside its own autofill list; without the
                           * token the conditional request above stays invisible
                           */
                          autoComplete={ conditionalActive ? 'username webauthn' : 'username' }
                          onChange={(e) => setEmail(e.target.value)} required
                        />
                        <label>{I18N.t("E-Mail-Adresse")}</label>
                      </dia-form-control>
                    </dia-form-line>
                    <dia-form-line>
                      <dia-form-control>
                        <input
                          type="password"
                          name="password"
                          placeholder={I18N.t("Geben Sie Ihr Passwort ein")}
                          autoComplete="current-password"
                          value={password} onChange={(e) => setPassword(e.target.value)} required
                        />
                        <label>{I18N.t("Passwort")}</label>
                      </dia-form-control>
                      <dia-form-control>
                        <button data-primary-dark disabled={ busy }>
                          { I18N.t("Anmelden") } <i data-icon-after="check"></i>
                        </button>
                      </dia-form-control>
                    </dia-form-line>
                    { loginError && (
                      <dia-form-line>
                        { /** inline, where the user is looking -- the old alert()
                              was a blocking dialog with no styling **/ }
                        <p role="alert" data-form-error>{ loginError }</p>
                      </dia-form-line>
                    ) }
                  </dia-form-area>
                  { /**
                      the two alternative ways in sit side by side in one row,
                      without the "oder"/label column each of them used to carry.
                      the labels said little the buttons do not already say, and
                      two stacked label-plus-button rows were what pushed the card
                      past the height its container can show
                    **/ }
                  <dia-form-area data-login-alternatives>
                    <dia-form-line>
                      { webAuthnAvailable && (
                        <dia-form-control>
                          <button
                            type="button"
                            data-secondary-light
                            data-login-passkey
                            disabled={ busy }
                            onClick={ handlePasswordlessPasskey }
                          >
                            { I18N.t("Mit Passkey anmelden") }
                            <i data-icon-after="fingerprint"></i>
                          </button>
                        </dia-form-control>
                      ) }
                      <dia-form-control>
                        <button
                          data-secondary-light
                          onClick={(e) => handleSSOLogin(e, AuthSsoProvider.MICROSOFT)}>
                          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 23 23">
                            <path fill="#f3f3f3" d="M0 0h23v23H0z"/><path fill="#f35325" d="M1 1h10v10H1z"/>
                            <path fill="#81bc06" d="M12 1h10v10H12z"/><path fill="#05a6f0" d="M1 12h10v10H1z"/>
                            <path fill="#ffba08" d="M12 12h10v10H12z"/>
                          </svg>
                          { I18N.t("Microsoft") }
                        </button>
                      </dia-form-control>
                    </dia-form-line>
                  </dia-form-area>
                </form>
              </dia-form>
            </dia-grid-nested>
            <dia-grid-nested>
              <img
                src="/logo-full-white.svg"
                alt="DIA Logo"
              />
              <small>
                Powered by
                <a
                  href="https://www.dia-software.de"
                  target="_blank"
                  rel="noreferrer"
                >
                  DIA Connecting Software
                </a>
              </small>
            </dia-grid-nested>
          </dia-tabs-panel>
          <input
            type="radio"
            name="tabset"
            id="dia-tab2"
            aria-controls="dia-tab-panel2"
            checked={ activeTab === 'reset' }
            disabled={ inChallenge }
            onChange={ () => setActiveTab('reset') }
          />
          <label htmlFor="dia-tab2">{I18N.t("Passwort vergessen?")}</label>
          <dia-tabs-panel id="dia-tab-panel2">
            <dia-grid-nested><ResetPasswordComponent /></dia-grid-nested>
            <dia-grid-nested>
              <img
                src="/logo-full-white.svg"
                alt="DIA Logo"
              />
              <small>
                Powered by
                <a
                  href="https://www.dia-software.de"
                  target="_blank"
                  rel="noreferrer"
                >
                  DIA Connecting Software
                </a>
              </small>
            </dia-grid-nested>
          </dia-tabs-panel>
          <input
            type="radio"
            name="tabset"
            id="dia-tab3"
            aria-controls="dia-tab-panel3"
            checked={ activeTab === 'twofactor' }
            /**
             * without a running challenge this tab has nothing to show and stays
             * out of the tab order entirely - the empty label already keeps it
             * away from the mouse
             */
            disabled={ !inChallenge }
            onChange={ () => setActiveTab('twofactor') }
          />
          <label htmlFor="dia-tab3" data-login-tab-twofactor>
            { inChallenge ? I18N.t("Bestätigung") : '' }
          </label>
          <dia-tabs-panel id="dia-tab-panel3">
            <dia-grid-nested>
              <dia-form>
                <form onSubmit={ handleTwoFactorSubmit } data-two-factor-form>
                  <dia-form-area>
                    { !useRecovery && hasTotp && (
                      <dia-form-line>
                        <dia-form-control>
                          <input
                            ref={ codeRef }
                            type="text"
                            name="code"
                            /**
                             * a numeric keypad on the phone, the code out of the
                             * sms/authenticator suggestion of the os, and a hard
                             * stop at the six digits the backend expects
                             */
                            inputMode="numeric"
                            autoComplete="one-time-code"
                            maxLength={ 6 }
                            placeholder={I18N.t("Einmal-Code")}
                            value={ code }
                            onChange={ (e) => setCode(e.target.value) }
                            required
                          />
                          <label>{I18N.t("Einmal-Code")}</label>
                        </dia-form-control>
                      </dia-form-line>
                    ) }
                    { useRecovery && hasRecovery && (
                      <dia-form-line>
                        <dia-form-control>
                          <input
                            ref={ recoveryRef }
                            type="text"
                            name="recovery_code"
                            /**
                             * recovery codes are not numeric and not six long, so
                             * none of the one-time-code hints apply here
                             */
                            autoComplete="off"
                            placeholder={I18N.t("Wiederherstellungscode")}
                            value={ recoveryCode }
                            onChange={ (e) => setRecoveryCode(e.target.value) }
                            required
                          />
                          <label>{I18N.t("Wiederherstellungscode")}</label>
                        </dia-form-control>
                      </dia-form-line>
                    ) }
                    <dia-form-line>
                      <dia-form-control data-two-factor-remember>
                        <input
                          type="checkbox"
                          id="two-factor-remember"
                          name="remember_device"
                          checked={ rememberDevice }
                          onChange={ (e) => setRememberDevice(e.target.checked) }
                        />
                        <label htmlFor="two-factor-remember">
                          {I18N.t("Diesem Gerät 30 Tage vertrauen")}
                        </label>
                      </dia-form-control>
                    </dia-form-line>
                    <dia-form-line>
                      <dia-form-control>
                        <button data-primary-dark disabled={ busy }>
                          {I18N.t("Prüfen")} <i data-icon-after="shield-check"></i>
                        </button>
                      </dia-form-control>
                      <dia-form-control>
                        <button
                          type="button"
                          data-secondary-light
                          data-two-factor-cancel
                          onClick={ () => resetChallenge() }
                        >
                          {I18N.t("Abbrechen")}
                        </button>
                      </dia-form-control>
                    </dia-form-line>
                    { twoFactorStatus && (
                      <dia-form-line>
                        { /** progress and success are announced, but must not
                              interrupt what the screen reader is reading **/ }
                        <p role="status" aria-live="polite" data-form-status>
                          { twoFactorStatus }
                        </p>
                      </dia-form-line>
                    ) }
                    { twoFactorError && (
                      <dia-form-line>
                        <p role="alert" data-form-error>{ twoFactorError }</p>
                      </dia-form-line>
                    ) }
                  </dia-form-area>
                  { (hasWebAuthn || hasRecovery) && (
                    <dia-form-area>
                      <dia-form-line>
                        { hasWebAuthn && (
                          <dia-form-control>
                            <button
                              ref={ passkeyRef }
                              type="button"
                              data-secondary-light
                              data-two-factor-passkey
                              disabled={ busy }
                              onClick={ handleSecondFactorPasskey }
                            >
                              { I18N.t("Mit Passkey bestätigen") }
                              <i data-icon-after="fingerprint"></i>
                            </button>
                          </dia-form-control>
                        ) }
                        { hasRecovery && (
                          <dia-form-control>
                            <button
                              type="button"
                              data-two-factor-recovery-toggle
                              /**
                               * aria-pressed instead of a second tab: it is the
                               * same form, only another code field
                               */
                              aria-pressed={ useRecovery }
                              onClick={ () => {
                                setTwoFactorError('');
                                setUseRecovery(!useRecovery);
                              } }
                            >
                              { useRecovery
                                ? I18N.t("Einmal-Code verwenden")
                                : I18N.t("Wiederherstellungscode verwenden") }
                            </button>
                          </dia-form-control>
                        ) }
                      </dia-form-line>
                    </dia-form-area>
                  ) }
                </form>
              </dia-form>
            </dia-grid-nested>
            <dia-grid-nested>
              <img
                src="/logo-full-white.svg"
                alt="DIA Logo"
              />
              <small>
                Powered by
                <a
                  href="https://www.dia-software.de"
                  target="_blank"
                  rel="noreferrer"
                >
                  DIA Connecting Software
                </a>
              </small>
            </dia-grid-nested>
          </dia-tabs-panel>
        </dia-tabs>

        <a
          data-login-network-participants
          href={ROUTE_CONFIG.NETWORK_PARTICIPANTS.path}
          target="_blank"
          rel="noreferrer"
        >
          {I18N.t("Netzwerkteilnehmer")}
          <span data-visually-hidden>{I18N.t("(öffnet in neuem Tab)")}</span>
        </a>

        <PartnerWall />

        <ul data-background>
          <li></li>
          <li></li>
          <li></li>
          <li></li>
          <li></li>
          <li></li>
          <li></li>
        </ul>
    </>
  );
}
